import { ScrollingModule } from '@angular/cdk/scrolling';
import { HttpClientModule } from '@angular/common/http';
import { ErrorHandler, NgModule } from '@angular/core';
import { MatPaginatorIntl } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule, Routes } from '@angular/router';
import { LetModule, PushModule } from '@ngrx/component';
import { EffectsModule } from '@ngrx/effects';
import { FullRouterStateSerializer, RouterStateSerializer, StoreRouterConnectingModule } from '@ngrx/router-store';
import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { ApplicationModeService, ScriptLoaderService } from '@uzurv/clara';
import { BugsnagService, SystemEventsService } from '@uzurv/core';
import { AuthGuard, UzurvRestApiModule, UzurvRestHttpInterceptorsModule } from '@uzurv/http';
import { BannerModule } from '../components/banner/banner.module';
import { RequiresPermissionsModule } from '../components/directives/requires-permissions';
import { MatPaginatorIntlNoLength } from '../components/generic-list';
import { ReservationEventService } from '../components/reservation';
import { MapsSdkResolver } from '../components/resolvers/maps-sdk.resolver';
import { TripDetailModule } from '../pages/trip-detail/trip-detail.module';
import { UzurvCrmHttpInterceptorsModule } from './../http-interceptors';
import { AppComponent } from './app.component';
import { MaterialModule } from './material.module';
import { CustomSerializer, reducers } from './store/app.reducer';
import { clearState } from './store/clear-state.reducer';
import { GoogleAnalyticsResolver } from '../components/resolvers/google-analytics.resolver';
import { CrmStoreModule } from './store/crm.store.module';
import { FeatureFlagEffects } from '../store/feature-flag';
import { featureFlagsReducer } from '../store/feature-flag/feature-flag.reducer';

const routes: Routes = [
  {
    path: '',
    loadChildren: () => import('../pages/container/container.module').then(m => m.ContainerModule),
    canActivate: [AuthGuard()],
    resolve: {
      mapsSdk: MapsSdkResolver,
      googleAnalytics: GoogleAnalyticsResolver
    }
  },
  {
    path: 'login',
    loadChildren: () => import('../components/login/login.module').then(m => m.LoginModule)
  },
  {
    path: '403',
    loadChildren: () => import('../pages/not-authorized/not-authorized.module').then(m => m.NotAuthorizedModule)
  },
  {
    path: 'maintenance-mode',
    loadChildren: () => import('../pages/maintenance-mode/maintenance-mode.module').then(m => m.MaintenanceModeModule)
  },
  {
    path: 'deprecation-mode',
    loadChildren: () => import('../pages/deprecation-mode/deprecation-mode.module').then(m => m.DeprecationModeModule)
  },
  {
    path: '**',
    redirectTo: '404'
  },
  {
    path: '404',
    loadChildren: () => import('../pages/not-found/not-found.module').then(m => m.NotFoundModule)
  }
];

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ScrollingModule,
    HttpClientModule,
    RouterModule.forRoot(routes, { onSameUrlNavigation: 'reload' }),
    StoreModule.forRoot(reducers, {
      runtimeChecks: {
        strictStateImmutability: false,
        strictActionImmutability: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
        strictActionWithinNgZone: false
      },
      metaReducers: [clearState]
    }),
    EffectsModule.forRoot([]),
    StoreRouterConnectingModule.forRoot({ serializer: FullRouterStateSerializer }),
    StoreDevtoolsModule.instrument(),
    LetModule,
    PushModule,
    UzurvRestApiModule,
    UzurvCrmHttpInterceptorsModule.forRoot(),
    UzurvRestHttpInterceptorsModule.forRoot(),
    BannerModule,
    MatProgressSpinnerModule,
    TripDetailModule,
    RequiresPermissionsModule,
    MaterialModule,
    CrmStoreModule,
    StoreModule.forFeature('featureFlags', featureFlagsReducer),
    EffectsModule.forFeature([FeatureFlagEffects])
  ],
  providers: [
    SystemEventsService,
    ReservationEventService,
    ScriptLoaderService,
    ApplicationModeService,
    MapsSdkResolver,
    GoogleAnalyticsResolver,
    { provide: RouterStateSerializer, useClass: CustomSerializer },
    { provide: MatPaginatorIntl, useClass: MatPaginatorIntlNoLength },
    { provide: ErrorHandler, deps: [BugsnagService], useFactory: bugsnagService => bugsnagService.getErrorHandler() }
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent]
})
export class AppModule {}
